import { InlineMessage, Tag } from "@fanduel/formation-react-components";
import {
  TimeCalendar,
  TimeClock,
  WeatherWind,
} from "@fanduel/formation-react-icons";
import { getNetworkDisplayName } from "@/components/headToHead/Header/GameInfo";
import TvSvg from "@/components/scoreboard/TvSvg";
import CoveredStadiumIcon from "@/components/svg/weather/CoveredStadium";
import WeatherIcon from "../../svg/weather/WeatherIcon";
import { NflGameState } from "../findGameState";
import { DayWeekStringClient, TimeStringClient } from "./TimeStringClient";
import type { Game } from "../types";
import styles from "./gameInfo.module.scss";

interface GameInfoProps {
  game: Game;
  isMobile?: boolean;
  gameState: NflGameState;
}

const INDOOR_CONDITIONS = ["Covered", "Indoor", "Controlled"];

const GameInfo = ({ game, isMobile, gameState }: GameInfoProps) => {
  const smallIconSize = 16;
  const { broadcastInfo, weather } = game;
  const { condition, humidity, tempInF, windSpeedInMph } = weather ?? {};
  const network = getNetworkDisplayName(
    isMobile ?? false,
    broadcastInfo?.network
  );
  const hasWeatherInfo = tempInF && condition;
  const isIndoor =
    condition &&
    INDOOR_CONDITIONS.some((indoorCondition) =>
      condition.includes(indoorCondition)
    );

  let isUpset = false;

  if (
    gameState === "POST" &&
    game.closingLines?.spread &&
    game.homeTeam?.stats?.points &&
    game.awayTeam?.stats?.points
  ) {
    const spread = game.closingLines.spread;
    const homeScore = game.homeTeam.stats.points;
    const awayScore = game.awayTeam.stats.points;
    if (spread < 0 && homeScore < awayScore) {
      isUpset = true;
    } else if (spread > 0 && homeScore > awayScore) {
      isUpset = true;
    }
  }

  return (
    <div className={styles.gameInfo}>
      <div className={styles.infoLeft}>
        <div>
          <TimeCalendar width={smallIconSize} height={smallIconSize} />
          <p>
            <DayWeekStringClient isoString={game?.scheduledTime} />
          </p>
        </div>
        {gameState === "PRE" && (
          <div>
            <TimeClock width={smallIconSize} height={smallIconSize} />
            <p>
              <TimeStringClient isoString={game?.scheduledTime} />
            </p>
          </div>
        )}
        {gameState === "LIVE" && (
          <div>
            <Tag intent="important">LIVE</Tag>
          </div>
        )}
        {gameState !== "POST" && (
          <div>
            <TvSvg />
            <p>{network}</p>
          </div>
        )}
      </div>
      {isUpset && (
        <InlineMessage className={styles.upsetAlert} intent="info">
          Upset Alert!
        </InlineMessage>
      )}
      {!hasWeatherInfo && isMobile ? null : (
        <div className={styles.infoRight}>
          {isIndoor ? (
            <div>
              <CoveredStadiumIcon
                width={smallIconSize}
                height={smallIconSize}
              />
              <p>Covered Stadium</p>
            </div>
          ) : (
            <div>
              <WeatherIcon
                width={smallIconSize}
                height={smallIconSize}
                condition={condition ?? "-"}
              />
              <span>{tempInF ?? "-"}ºF</span>
              <span>&nbsp;|&nbsp;</span>
              <span>{humidity ?? "-"}% Humidity</span>
            </div>
          )}
          {!isIndoor && (
            <div>
              <WeatherWind width={smallIconSize} height={smallIconSize} />
              <p>{`${windSpeedInMph ?? "-"}mph`}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GameInfo;
